<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import InboxesAPI from 'dashboard/api/inboxes';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
});

const store = useStore();
const { t } = useI18n();

const voiceEnabled = ref(props.inbox.voice_enabled || false);
const inboundCallsEnabled = ref(props.inbox.inbound_calls_enabled !== false);
const apiKeySid = ref(props.inbox.api_key_sid || '');
const apiKeySecret = ref('');
const isUpdating = ref(false);
const isTogglingInbound = ref(false);

const isVoiceConfigured = computed(() => !!props.inbox.voice_configured);
const hasApiKeySid = computed(() => !!props.inbox.api_key_sid);
const hasExistingCredentials = computed(
  () => hasApiKeySid.value && !!props.inbox.has_api_key_secret
);
const needsCredentials = computed(
  () =>
    voiceEnabled.value &&
    !isVoiceConfigured.value &&
    !hasExistingCredentials.value
);
const needsApiKeySid = computed(
  () => needsCredentials.value && !hasApiKeySid.value
);
const isSubmitDisabled = computed(() => {
  if (!voiceEnabled.value) return false;
  if (needsCredentials.value) {
    if (needsApiKeySid.value && !apiKeySid.value) return true;
    return !apiKeySecret.value;
  }
  return false;
});

watch(
  () => props.inbox.voice_enabled,
  value => {
    voiceEnabled.value = value || false;
  }
);
watch(
  () => props.inbox.api_key_sid,
  value => {
    apiKeySid.value = value || '';
  }
);
watch(
  () => props.inbox.inbound_calls_enabled,
  value => {
    inboundCallsEnabled.value = value !== false;
  }
);

const handleInboundToggle = async newValue => {
  if (isTogglingInbound.value) return;
  const previousValue = inboundCallsEnabled.value;
  inboundCallsEnabled.value = newValue;
  isTogglingInbound.value = true;
  try {
    await InboxesAPI.setInboundCalls(props.inbox.id, newValue);
    await store.dispatch('inboxes/get', props.inbox.id);
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (_) {
    inboundCallsEnabled.value = previousValue;
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isTogglingInbound.value = false;
  }
};

const updateVoiceSettings = async () => {
  isUpdating.value = true;
  try {
    const channelPayload = { voice_enabled: voiceEnabled.value };

    if (needsCredentials.value) {
      if (needsApiKeySid.value) {
        channelPayload.api_key_sid = apiKeySid.value;
      }
      channelPayload.api_key_secret = apiKeySecret.value;
    }

    await store.dispatch('inboxes/updateInbox', {
      id: props.inbox.id,
      formData: false,
      channel: channelPayload,
    });
    apiKeySecret.value = '';
    useAlert(t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'));
  } catch (_) {
    useAlert(t('INBOX_MGMT.EDIT.API.ERROR_MESSAGE'));
  } finally {
    isUpdating.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-6">
    <SettingsToggleSection
      v-model="voiceEnabled"
      :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.LABEL')"
      :description="
        $t('INBOX_MGMT.VOICE_CONFIGURATION.ENABLE_VOICE.DESCRIPTION')
      "
    />

    <div v-if="voiceEnabled && needsCredentials" class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.VOICE_CONFIGURATION.CREDENTIALS.DESCRIPTION') }}
      </p>
      <NextInput
        v-if="needsApiKeySid"
        v-model="apiKeySid"
        :label="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.LABEL')"
        :placeholder="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SID.PLACEHOLDER')"
      />
      <NextInput
        v-model="apiKeySecret"
        type="password"
        :label="$t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.LABEL')"
        :placeholder="
          $t('INBOX_MGMT.ADD.VOICE.TWILIO.API_KEY_SECRET.PLACEHOLDER')
        "
      />
    </div>

    <div
      v-if="props.inbox.voice_enabled"
      class="relative"
      :class="{ 'pointer-events-none opacity-60': isTogglingInbound }"
    >
      <SettingsToggleSection
        :model-value="inboundCallsEnabled"
        :header="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.LABEL')"
        :description="$t('INBOX_MGMT.VOICE_CONFIGURATION.INBOUND.DESCRIPTION')"
        :hide-toggle="isTogglingInbound"
        @update:model-value="handleInboundToggle"
      >
        <template v-if="isTogglingInbound" #hiddenToggle>
          <Spinner class="size-4 text-n-slate-11" />
        </template>
      </SettingsToggleSection>
    </div>

    <div v-if="props.inbox.voice_enabled && props.inbox.voice_call_webhook_url">
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_TITLE')"
        :help-text="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_VOICE_URL_SUBTITLE')
        "
      >
        <woot-code :script="props.inbox.voice_call_webhook_url" lang="html" />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_TITLE')
        "
        :help-text="
          $t('INBOX_MGMT.ADD.VOICE.CONFIGURATION.TWILIO_STATUS_URL_SUBTITLE')
        "
      >
        <woot-code :script="props.inbox.voice_status_webhook_url" lang="html" />
      </SettingsFieldSection>
    </div>

    <div>
      <NextButton
        :disabled="isSubmitDisabled"
        :is-loading="isUpdating"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')"
        @click="updateVoiceSettings"
      />
    </div>
  </div>
</template>
